import React, { createContext, useState, useCallback, useContext, useEffect } from 'react';
import { useAuth } from './auth.context';
import {
  accountabilityService,
  goalsService,
  achievementsService,
} from '../services/service-proxy';
import { updateStreak } from '../utils/streak.util';
import logger from '../utils/logger.util';

const {
  getPartnership,
  getPendingRequests,
  findPartner,
  sendPartnerRequest,
  acceptPartnerRequest,
  declinePartnerRequest,
  endPartnership,
  submitCheckIn,
  getCheckIns,
  getTodayCheckIn,
} = accountabilityService;

const {
  createGoal,
  getGoals,
  updateGoalProgress,
} = goalsService;
const completeGoalService = goalsService.completeGoal;
const abandonGoalService = goalsService.abandonGoal;
const deleteGoalService = goalsService.deleteGoal;

const {
  getAchievements,
  getUnseenAchievements,
  checkAndAwardAchievements,
  markAchievementSeen,
  getAchievementDefinitions,
} = achievementsService;

const AccountabilityContext = createContext({
  partnership: null,
  pendingRequests: [],
  goals: [],
  completedGoals: [],
  achievements: [],
  unseenAchievements: [],
  allDefinitions: [],
  streak: { currentStreak: 0, longestStreak: 0 },
  checkIns: [],
  todayCheckIn: null,
  partnerTodayCheckIn: null,
  loading: true,
  findingPartner: false,
  handleFindPartner: () => {},
  handleSendRequest: () => {},
  handleAcceptRequest: () => {},
  handleDeclineRequest: () => {},
  handleEndPartnership: () => {},
  handleSubmitCheckIn: () => {},
  handleCreateGoal: () => {},
  handleUpdateGoalProgress: () => {},
  handleCompleteGoal: () => {},
  handleAbandonGoal: () => {},
  handleDeleteGoal: () => {},
  handleDismissAchievement: () => {},
  triggerAchievementCheck: () => {},
  refreshData: () => {},
});

export const AccountabilityProvider = ({ children }) => {
  const { user, profile } = useAuth();
  const [partnership, setPartnership] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [goals, setGoals] = useState([]);
  const [completedGoals, setCompletedGoals] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [unseenAchievements, setUnseenAchievements] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [todayCheckIn, setTodayCheckIn] = useState(null);
  const [partnerTodayCheckIn, setPartnerTodayCheckIn] = useState(null);
  const [streak, setStreak] = useState({ currentStreak: 0, longestStreak: 0 });
  const [loading, setLoading] = useState(true);
  const [findingPartner, setFindingPartner] = useState(false);

  const allDefinitions = getAchievementDefinitions();

  // Load all accountability data when user is authenticated
  const loadData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Load partnership, goals, and achievements in parallel
      const [partnershipData, pendingData, activeGoals, completedGoalsData, achievementsData, unseenData] =
        await Promise.all([
          getPartnership(user.uid).catch(() => null),
          getPendingRequests(user.uid).catch(() => []),
          getGoals(user.uid, 'active').catch(() => []),
          getGoals(user.uid, 'completed').catch(() => []),
          getAchievements(user.uid).catch(() => []),
          getUnseenAchievements(user.uid).catch(() => []),
        ]);

      setPartnership(partnershipData);
      setPendingRequests(pendingData || []);
      setGoals(activeGoals || []);
      setCompletedGoals(completedGoalsData || []);
      setAchievements(achievementsData || []);
      setUnseenAchievements(unseenData || []);

      // Load streak from profile
      if (profile) {
        setStreak({
          currentStreak: profile.currentStreak || 0,
          longestStreak: profile.longestStreak || 0,
        });
      }

      // Load check-ins if partnership exists
      if (partnershipData) {
        try {
          const checkInsResult = await getCheckIns(partnershipData.id);
          setCheckIns(checkInsResult?.checkIns || []);

          const myCheckIn = await getTodayCheckIn(partnershipData.id, user.uid);
          setTodayCheckIn(myCheckIn);

          // Get partner's check-in
          const partnerId = partnershipData.users.find((id) => id !== user.uid);
          if (partnerId) {
            const partnerCheckIn = await getTodayCheckIn(partnershipData.id, partnerId);
            setPartnerTodayCheckIn(partnerCheckIn);
          }
        } catch (e) {
          logger.error('Failed to load check-ins:', e);
        }
      }
    } catch (error) {
      logger.error('Failed to load accountability data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, profile]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFindPartner = useCallback(async () => {
    if (!user) return null;
    setFindingPartner(true);
    try {
      const candidate = await findPartner(user.uid);
      return candidate;
    } catch (error) {
      logger.error('Failed to find partner:', error);
      return null;
    } finally {
      setFindingPartner(false);
    }
  }, [user]);

  const handleSendRequest = useCallback(async (toUser) => {
    if (!user || !profile) return;
    try {
      const result = await sendPartnerRequest(
        user.uid,
        toUser.id,
        { displayName: profile.displayName, username: profile.username, avatarUrl: profile.avatarUrl },
        { displayName: toUser.displayName, username: toUser.username, avatarUrl: toUser.avatarUrl }
      );
      return result;
    } catch (error) {
      logger.error('Failed to send partner request:', error);
      throw error;
    }
  }, [user, profile]);

  const handleAcceptRequest = useCallback(async (pairId) => {
    try {
      await acceptPartnerRequest(pairId);
      await loadData();
    } catch (error) {
      logger.error('Failed to accept request:', error);
      throw error;
    }
  }, [loadData]);

  const handleDeclineRequest = useCallback(async (pairId) => {
    try {
      await declinePartnerRequest(pairId);
      setPendingRequests((prev) => prev.filter((r) => r.id !== pairId));
    } catch (error) {
      logger.error('Failed to decline request:', error);
      throw error;
    }
  }, []);

  const handleEndPartnership = useCallback(async () => {
    if (!partnership) return;
    try {
      await endPartnership(partnership.id);
      setPartnership(null);
      setCheckIns([]);
      setTodayCheckIn(null);
      setPartnerTodayCheckIn(null);
    } catch (error) {
      logger.error('Failed to end partnership:', error);
      throw error;
    }
  }, [partnership]);

  const handleSubmitCheckIn = useCallback(async (checkInData) => {
    if (!partnership || !user) return;
    try {
      const result = await submitCheckIn(partnership.id, {
        ...checkInData,
        userId: user.uid,
      });
      setTodayCheckIn(result);
      setCheckIns((prev) => [result, ...prev]);

      // Update streak
      if (profile) {
        const newStreak = await updateStreak(user.uid, profile);
        setStreak({
          currentStreak: newStreak.currentStreak,
          longestStreak: newStreak.longestStreak,
        });
      }

      return result;
    } catch (error) {
      logger.error('Failed to submit check-in:', error);
      throw error;
    }
  }, [partnership, user, profile]);

  const handleCreateGoal = useCallback(async (goalData) => {
    if (!user) return;
    try {
      const result = await createGoal(user.uid, goalData);
      setGoals((prev) => [result, ...prev]);
      return result;
    } catch (error) {
      logger.error('Failed to create goal:', error);
      throw error;
    }
  }, [user]);

  const handleUpdateGoalProgress = useCallback(async (goalId, currentValue) => {
    if (!user) return;
    try {
      const result = await updateGoalProgress(user.uid, goalId, currentValue);
      setGoals((prev) => prev.map((g) => (g.id === goalId ? result : g)));
      return result;
    } catch (error) {
      logger.error('Failed to update goal progress:', error);
      throw error;
    }
  }, [user]);

  const handleCompleteGoal = useCallback(async (goalId) => {
    if (!user) return;
    try {
      const result = await completeGoalService(user.uid, goalId);
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      setCompletedGoals((prev) => [result, ...prev]);

      // Check for goal-related achievements
      const newAchievements = await checkAndAwardAchievements(user.uid, 'goal_completed', {
        count: completedGoals.length + 1,
      });
      if (newAchievements.length > 0) {
        setUnseenAchievements((prev) => [...prev, ...newAchievements]);
        setAchievements((prev) => [...prev, ...newAchievements]);
      }

      return result;
    } catch (error) {
      logger.error('Failed to complete goal:', error);
      throw error;
    }
  }, [user, completedGoals]);

  const handleAbandonGoal = useCallback(async (goalId) => {
    if (!user) return;
    try {
      await abandonGoalService(user.uid, goalId);
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
    } catch (error) {
      logger.error('Failed to abandon goal:', error);
      throw error;
    }
  }, [user]);

  const handleDeleteGoal = useCallback(async (goalId) => {
    if (!user) return;
    try {
      await deleteGoalService(user.uid, goalId);
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      setCompletedGoals((prev) => prev.filter((g) => g.id !== goalId));
    } catch (error) {
      logger.error('Failed to delete goal:', error);
      throw error;
    }
  }, [user]);

  const handleDismissAchievement = useCallback(async (achievementId) => {
    if (!user) return;
    try {
      await markAchievementSeen(user.uid, achievementId);
      setUnseenAchievements((prev) => prev.filter((a) => a.id !== achievementId));
    } catch (error) {
      logger.error('Failed to dismiss achievement:', error);
    }
  }, [user]);

  const triggerAchievementCheck = useCallback(async (eventType, eventData) => {
    if (!user) return [];
    try {
      const newAchievements = await checkAndAwardAchievements(user.uid, eventType, eventData);
      if (newAchievements.length > 0) {
        setUnseenAchievements((prev) => [...prev, ...newAchievements]);
        setAchievements((prev) => [...prev, ...newAchievements]);
      }
      return newAchievements;
    } catch (error) {
      logger.error('Failed to check achievements:', error);
      return [];
    }
  }, [user]);

  const value = {
    partnership,
    pendingRequests,
    goals,
    completedGoals,
    achievements,
    unseenAchievements,
    allDefinitions,
    streak,
    checkIns,
    todayCheckIn,
    partnerTodayCheckIn,
    loading,
    findingPartner,
    handleFindPartner,
    handleSendRequest,
    handleAcceptRequest,
    handleDeclineRequest,
    handleEndPartnership,
    handleSubmitCheckIn,
    handleCreateGoal,
    handleUpdateGoalProgress,
    handleCompleteGoal,
    handleAbandonGoal,
    handleDeleteGoal,
    handleDismissAchievement,
    triggerAchievementCheck,
    refreshData: loadData,
  };

  return (
    <AccountabilityContext.Provider value={value}>
      {children}
    </AccountabilityContext.Provider>
  );
};

export const useAccountability = () => {
  return useContext(AccountabilityContext);
};

export default AccountabilityContext;
